import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import Box from "@mui/material/Box";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import {
  getProducts,
  getPriceById,
  createCheckoutSession,
  createPortalSession,
  createCheckoutPaymentMethod,
  cancelSubscription,
} from "../Services/API/stripe";
import {
  getPaymentRecords,
  getCustomerSubscriptions,
} from "../Services/API/customer";
import { authJWT } from "../Services/API/auth";
import ResponsiveAppBar from "../Components/ResponsiveAppBar";
import { Stack, Tooltip } from "@mui/material";

export default function Products() {
  let { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [customer, setCustomer] = useState();
  const [selectedProduct, setSelectedProduct] = useState({});
  const [seletedDefaultPrice, setSelectedDefaultPrice] = useState({});
  const [products, setProducts] = useState([]);
  const [memberships, setMemberships] = useState([]);
	const [lastCanceledMemberShip,setLastCanceledMemberShip] = useState(undefined);
  const [paymentrecords, setPaymentrecords] = useState([]);
  const [hasPilotPlan, setHasPilotPlan] = useState(false);
  const [open, setOpen] = React.useState(false);
  const [openCancelSubDialog, setOpenCancelSubsDialog] = React.useState(false);
  const [openCancelSubSuccessDialog, setOpenCancelSubSuccessDialog] =
    React.useState(false);
  const [showProgressingBar, setShowProgressingBar] = React.useState(false);
  const [hasMembership, setHasMembership] = React.useState(false);
  const [hasPastDue, setHasPastDue] = React.useState(false);
  const handleProductClick = async (event, index, product) => {
    handleProgressingBarOpen();
    setSelectedIndex(index);
    const defaultPrice = await getPriceById(product.default_price);
    setSelectedDefaultPrice(defaultPrice.price);
    setSelectedProduct(product);
    handleProgressingBarClose();
    setOpen(true);
  };
  const handleCancelClick = async (event, index, product) => {
    handleProgressingBarOpen();
    setSelectedIndex(index);
    setSelectedProduct(product);
    handleProgressingBarClose();
    setOpenCancelSubsDialog(true);
  };
  useEffect(() => {
    async function init() {
      handleProgressingBarOpen();
      const successSessionID = searchParams.get("session_id");
      const authRes = await authJWT(token);
      setCustomer(authRes.customer);
      let activeProducts = [];
      activeProducts = await getProducts();
      const paymentrecordsRes = await getPaymentRecords(
        authRes.customer.CustomerId
      );
      for (const r of paymentrecordsRes?.PaymentRecords) {
        if (successSessionID === r.checkoutSessionID) {
          r.isSuccessSession = true;
        } else {
          r.isSuccessSession = false;
        }
      }
      setHasPilotPlan(paymentrecordsRes.HasPilotPlan);
      const customerSubscriptions = await getCustomerSubscriptions(
        authRes.customer.CustomerId
      );
			setLastCanceledMemberShip(findLastCanceledSubscription(customerSubscriptions))
      let membershipsArray = [];
      let productsArray = [];
      if (authRes.customer?.FromPatientApp) {
        for (const activeProduct of activeProducts.products) {
          if (activeProduct?.metadata?.PatientAccess) {
            if (activeProduct.type === "one-time") {
              productsArray.push(activeProduct);
            } else if (activeProduct.type === "subscription") {
              membershipsArray.push(activeProduct);
            }
          }
        }
      } else {
        for (const activeProduct of activeProducts.products) {
          if (activeProduct.type === "one-time") {
            productsArray.push(activeProduct);
          } else if (activeProduct.type === "subscription") {
            membershipsArray.push(activeProduct);
          }
        }
      }
      for (const membershipObj of membershipsArray) {
        membershipObj.isActiveSubscription = false;
        membershipObj.cancelledInBenefits = false;
        membershipObj.pastDue = false;
        for (const customerSubscription of customerSubscriptions) {
          if (
            membershipObj.id === customerSubscription?.plan?.product &&
            membershipObj.isActiveSubscription != true
          ) {
            membershipObj.existingSubscription = customerSubscription;
            if (
              customerSubscription.status === "active" ||
              customerSubscription.status === "trialing"
            ) {
              setHasMembership(true);
              membershipObj.isActiveSubscription = true;
            } else if (
              customerSubscription.status === "canceled" &&
              customerSubscription?.current_period_end >
                Math.floor(Date.now() / 1000) &&
              customerSubscription.latestInvoicePaid
            ) {
              membershipObj.cancelledInBenefits = true;
            } else if (customerSubscription.status === "past_due") {
              setHasPastDue(true);
              membershipObj.pastDue = true;
            }
          }
        }
      }
      setPaymentrecords(paymentrecordsRes?.PaymentRecords);
      setMemberships(membershipsArray);
      setProducts(productsArray);
      handleProgressingBarClose();
    }
    init();
  }, []);

  const findLastCanceledSubscription = (data) => {
    const latest = data
      .filter((item) => item.canceled_at != null)
      .reduce(
        (latest, item) =>
          !latest || item.canceled_at > latest.canceled_at ? item : latest,
        null
      );

    if (latest) {
      return latest;
    } else {
      return undefined;
    }
  };

  const handleClose = () => {
    setOpen(false);
  };
  const handleCloseCancelSubDialog = () => {
    setOpenCancelSubsDialog(false);
  };
  const handleCloseCancelSubSuccessDialog = () => {
    setOpenCancelSubSuccessDialog(false);
    window.location.reload(false);
  };

  const handleProgressingBarClose = () => {
    setShowProgressingBar(false);
  };
  const handleProgressingBarOpen = () => {
    setShowProgressingBar(true);
  };

  const selfManageClick = async () => {
    setShowProgressingBar(true);
    const portalSessionRes = await createPortalSession(customer.CustomerId);
    setShowProgressingBar(false);
    window.open(portalSessionRes.session.url, "_blank", "noreferrer");
  };

  const setupPaymentMethod = async () => {
    setShowProgressingBar(true);
    const setupPaymentMethodSession = await createCheckoutPaymentMethod(
      customer.CustomerId
    );
    setShowProgressingBar(false);
    window.open(setupPaymentMethodSession.url, "_blank", "noreferrer");
  };

  const purchaseProduct = async () => {
    handleProgressingBarOpen();
    let checkoutMode;
    if (seletedDefaultPrice.type === "one_time") {
      checkoutMode = "payment";
    } else if (seletedDefaultPrice.type === "recurring") {
      checkoutMode = "subscription";
    }
    const checkoutsession = await createCheckoutSession(
      customer.CustomerId,
      customer.StripeCustomerId,
      seletedDefaultPrice.id,
      selectedProduct.name,
      checkoutMode,
      customer.ORGID,
      token
    );
    handleProgressingBarClose();
    window.location.href = checkoutsession.session.url;
    // window.location.replace(checkoutsession.session.url);
    // window.open(checkoutsession.session.url, "_blank", "noreferrer");
    // setOpen(false);
    // window.close();
  };

  const handleCancelSubscription = async () => {
    handleCloseCancelSubDialog();
    handleProgressingBarOpen();

    // const checkoutsession = await createCheckoutSession( customer.CustomerId, customer.StripeCustomerId,seletedDefaultPrice.id, selectedProduct.name, checkoutMode, customer.ORGID, token);

    const resCancel = await cancelSubscription(
      selectedProduct?.existingSubscription?.id
    );
    handleProgressingBarClose();
    //handleProgressingBarClose();
    setOpenCancelSubSuccessDialog(true);
    // window.location.href = checkoutsession.session.url;
    // window.location.replace(checkoutsession.session.url);
    // window.open(checkoutsession.session.url, "_blank", "noreferrer");
    // setOpen(false);
    // window.close();
  };

  return (
    <Box sx={{ width: "100%", bgcolor: "background.paper" }}>
      <Backdrop sx={{ zIndex: 9999 }} open={showProgressingBar}>
        <CircularProgress />
      </Backdrop>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Purchase following product?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Product Name: {selectedProduct.name}
            <br></br>
            Price Amount: ${seletedDefaultPrice.unit_amount / 100}
            <br></br>
            Currency: {seletedDefaultPrice.currency}
            <br></br>
            Price Type: {seletedDefaultPrice.type}
          </DialogContentText>
          {seletedDefaultPrice.recurring && (
            <DialogContentText id="alert-dialog-description">
              Recurring Interval: {seletedDefaultPrice.recurring.interval}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={purchaseProduct} variant="contained" autoFocus>
            Purchase
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openCancelSubDialog}
        onClose={handleCloseCancelSubDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Cancel"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to cancel?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelSubDialog} variant="outlined">
            Go Back
          </Button>
          <Button
            onClick={handleCancelSubscription}
            variant="contained"
            autoFocus
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openCancelSubSuccessDialog}
        onClose={handleCloseCancelSubSuccessDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Cancel"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Your membership has been cancelled.
            <br />
            You'll have access to your benefits till
            <br />
            the end of your billing period.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseCancelSubSuccessDialog}
            variant="contained"
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <ResponsiveAppBar
        onEditClick={setupPaymentMethod}
        onViewClick={selfManageClick}
      ></ResponsiveAppBar>
      <Box paddingTop={8}>
        {customer && (
          <Box
            sx={{
              paddingX: 3,
              paddingTop: 2,
              display: "flex",
            }}
          >
            <Typography
              color="primary"
              sx={{
                fontSize: 18,
                color: "secondary.main",
                fontFamily: "ProximaNovaSoftSemibold",
              }}
            >
              Patient:
            </Typography>
            <Typography
              sx={{
                fontSize: 18,
                paddingX: 1,
                fontFamily: "ProximaNovaSoftRegular",
              }}
            >
              {customer.FirstName}, {customer.LastName}
            </Typography>
          </Box>
        )}
        {memberships && memberships.length > 0 && (
          <Typography
            sx={{
              fontSize: 18,
              paddingX: 3,
              paddingBottom: 1,
              fontFamily: "ProximaNovaSemibold",
              color: "dark.main",
            }}
          >
            Memberships
          </Typography>
        )}

        <Grid container spacing={2} paddingX={3}>
          {memberships &&
            memberships.length > 0 &&
            memberships.map(
              (membership, index) =>
                membership.default_price && (
                  <Grid item xs={6} md={4} lg={3} key={index} padding={0}>
                    <Paper
                      sx={{
                        height: { xs: 220, md: 200 },
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 2,
                        padding: { xs: 1, md: 2 },
                        alignItems: "center",
                      }}
                      elevation={3}
                    >
                      <Box>
                        <Typography
                          sx={{
                            textAlign: "center",
                            fontSize: {
                              xs: 16,
                              md: 18,
                            },
                            fontFamily: "ProximaNovaMedium",
                            color: "secondary.main",
                            lineHeight: 1.25,
                          }}
                        >
                          {membership.name}
                        </Typography>
                        <Typography
                          sx={{
                            textAlign: "center",
                            fontSize: {
                              xs: 14,
                              md: 16,
                            },
                            fontFamily: "ProximaNovaMedium",
                            color: "gray.main",
                            lineHeight: 1.25,
                          }}
                        >
                          {membership.type}
                        </Typography>
                        <Typography
                          sx={{
                            textAlign: "center",
                            fontSize: {
                              xs: 16,
                              md: 18,
                            },
                            fontFamily: "ProximaNovaMedium",
                            color: "darkGray.main",
                            lineHeight: 1.5,
                          }}
                        >
                          ${membership.unit_amount}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          flexGrow: 1,
                          alignItems: "end",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "end",
                        }}
                      >
                        {membership.isActiveSubscription && (
                          <Box sx={{ textAlign: "center" }}>
                            <Typography
                              sx={{
                                fontSize: 16,
                                lineHeight: 2,
                                fontFamily: "ProximaNovaSemibold",
                                color: "success.main",
                              }}
                            >
                              Active
                            </Typography>
                            <Button
                              variant="contained"
                              sx={{
                                fontFamily: "ProximaNovaSoftSemibold",
                                fontSize: {
                                  xs: 16,
                                  md: 18,
                                },
                                lineHeight: 1.25,
                                padding: 1,
                              }}
                              onClick={(event) =>
                                handleCancelClick(event, index, membership)
                              }
                            >
                              Cancel
                            </Button>
                          </Box>
                        )}
                        {!membership.isActiveSubscription &&
                          membership.pastDue &&
                          !membership.cancelledInBenefits &&
                          !hasMembership && (
                            <Box sx={{ textAlign: "center" }}>
                              <Typography
                                sx={{
                                  fontSize: 16,
                                  lineHeight: 1.5,
                                  fontFamily: "ProximaNovaSemibold",
                                  color: "error.main",
                                }}
                              >
                                Past-due
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 14,
                                  lineHeight: 2,
                                  fontFamily: "ProximaNovaSemibold",
                                  color: "darkGray.main",
                                }}
                              >
                                Payment failed
                              </Typography>
                              <Button
                                variant="contained"
                                sx={{
                                  fontFamily: "ProximaNovaSoftSemibold",
                                  fontSize: {
                                    xs: 16,
                                    md: 18,
                                  },
                                  lineHeight: 1.25,
                                  padding: 1,
                                  height: 32,
                                }}
                                onClick={(event) =>
                                  handleCancelClick(event, index, membership)
                                }
                              >
                                Cancel
                              </Button>
                            </Box>
                          )}
                        {!membership.isActiveSubscription &&
                          membership.cancelledInBenefits &&
                          !hasMembership && (
                            <Box sx={{ textAlign: "center" }}>
                              <Typography
                                sx={{
                                  fontSize: 16,
                                  lineHeight: 1.5,
                                  fontFamily: "ProximaNovaSemibold",
                                  color: "error.main",
                                }}
                              >
                                Cancelled
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: {
                                    xs: 12,
                                    md: 14,
                                  },
                                  fontFamily: "ProximaNova",
                                  color: "gray.main",
                                  lineHeight: 1.1,
                                }}
                              >
                                Membership benefits are active until the end of
                                the billing period.
                              </Typography>
                            </Box>
                          )}

                        {!hasMembership && !hasPastDue && (
                          <Box
                            sx={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "center",
                              padding: 1,
                            }}
                          >
                            <Button
                              variant="contained"
                              sx={{
                                fontFamily: "ProximaNovaSoftSemibold",
                                fontSize: {
                                  xs: 16,
                                  md: 18,
                                },
                                lineHeight: 1.25,
                              }}
                              color={
                                !membership.isActiveSubscription &&
                                !hasMembership &&
                                membership.id === lastCanceledMemberShip?.plan?.product
                                  ? "success"
                                  : "primary"
                              }
                              onClick={(event) =>
                                handleProductClick(event, index, membership)
                              }
                            >
                              {!membership.isActiveSubscription &&
                              !hasMembership &&
                              membership.id === lastCanceledMemberShip?.plan?.product
                                ? "Re-Subscribe"
                                : "Purchase"}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                )
            )}
        </Grid>
        <Typography
          sx={{
            fontSize: 18,
            fontFamily: "ProximaNovaSoftMedium",
            lineHeight: 1.25,
            color: "darkGray.main",
            padding: 3,
          }}
        >
          Note - If a membership is cancelled, benefits will continue until the
          end of the billing period. If a new membership is purchased within
          that time, the benefits of the new membership will apply and the
          benefits previously active will end.
        </Typography>
        {products && products.length > 0 && (
          <Box>
            <Typography
              sx={{
                fontSize: 18,
                paddingX: 3,
                paddingBottom: 1,
                fontFamily: "ProximaNovaSemibold",
                color: "dark.main",
              }}
            >
              Products
            </Typography>
          </Box>
        )}
        <Grid container spacing={2} paddingX={3}>
          {products &&
            products.length > 0 &&
            products.map(
              (product, index) =>
                product.default_price && (
                  <Grid item xs={6} md={4} lg={3} key={index} padding={0}>
                    <Paper
                      sx={{
                        height: 200,
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 2,
                        padding: { xs: 1, md: 2 },
                        alignItems: "center",
                      }}
                      elevation={3}
                    >
                      <Box>
                        <Typography
                          sx={{
                            textAlign: "center",
                            fontSize: {
                              xs: 16,
                              md: 18,
                            },
                            fontFamily: "ProximaNovaMedium",
                            color: "secondary.main",
                            lineHeight: 1.25,
                          }}
                        >
                          {product.name}
                        </Typography>
                        <Typography
                          sx={{
                            textAlign: "center",
                            fontSize: {
                              xs: 14,
                              md: 16,
                            },
                            fontFamily: "ProximaNovaMedium",
                            color: "gray.main",
                            lineHeight: 1.25,
                          }}
                        >
                          {product.type}
                        </Typography>
                        <Typography
                          sx={{
                            textAlign: "center",
                            fontSize: {
                              xs: 16,
                              md: 18,
                            },
                            fontFamily: "ProximaNovaMedium",
                            color: "darkGray.main",
                            lineHeight: 1.5,
                          }}
                        >
                          ${product.unit_amount}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          flexGrow: 1,
                          alignItems: "end",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "end",
                        }}
                      >
                        {hasPilotPlan &&
                          product.id ==
                            process.env.REACT_APP_STRIPE_POLITID && (
                            <Typography
                              sx={{
                                fontSize: 16,
                                lineHeight: 2,
                                fontFamily: "ProximaNovaSemibold",
                                color: "success.main",
                                textAlign: "center",
                                width: "100%",
                              }}
                            >
                              Active
                            </Typography>
                          )}
                        <Button
                          variant="contained"
                          sx={{
                            fontFamily: "ProximaNovaSoftSemibold",
                            fontSize: {
                              xs: 16,
                              md: 18,
                            },
                            lineHeight: 1.25,
                          }}
                          onClick={(event) =>
                            handleProductClick(event, index, product)
                          }
                        >
                          Purchase
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                )
            )}
        </Grid>
        <Box>
          <Typography
            sx={{
              fontSize: 18,
              paddingX: 3,
              paddingTop: 3,
              fontFamily: "ProximaNovaSemibold",
              color: "dark.main",
            }}
          >
            Recent Purchase History
          </Typography>
        </Box>

        <Stack
          sx={{
            px: 3,
            py: 1,
          }}
          direction={"column"}
          spacing={1}
        >
          {paymentrecords.map((record, index) => (
            <Paper
              key={index}
              elevation={3}
              sx={{
                padding: 1,
                borderRadius: 2,
              }}
            >
              <Grid container>
                <Grid
                  item
                  xs={4}
                  md={3}
                  sx={{
                    alignItems: "center",
                    justifyContent: "left",
                    display: "flex",
                  }}
                >
                  <Typography
                    sx={{
                      textAlign: "left",
                      fontFamily: "ProximaNovaSemibold",
                      fontSize: 16,
                      color: "darkGray.main",
                      lineHeight: 1.25,
                      display: "-webkit-box",
                      overflow: "hidden",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 2,
                    }}
                  >
                    {record.prodName}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={3}
                  md={3}
                  sx={{
                    alignItems: "center",
                    justifyContent: "center",
                    display: "flex",
                  }}
                >
                  <Stack direction={"column"}>
                    <Typography
                      sx={{
                        textAlign: "center",
                        fontFamily: "ProximaNovaMedium",
                        fontSize: 16,
                        lineHeight: 1.25,
                        color: "darkGray.main",
                      }}
                    >
                      ${record.unitPrice / 100}
                    </Typography>
                    <Typography
                      sx={{
                        textAlign: "center",
                        fontFamily: "ProximaNovaMedium",
                        fontSize: 14,
                        lineHeight: 1.25,
                        color: "gray.main",
                      }}
                    >
                      {record.type}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={4}
                  md={2}
                  sx={{
                    alignItems: "center",
                    justifyContent: "center",
                    display: "flex",
                  }}
                >
                  <Stack direction={"column"}>
                    <Typography
                      sx={{
                        textAlign: "center",
                        fontFamily: "ProximaNovaMedium",
                        fontSize: 16,
                        lineHeight: 1.25,
                        color: "darkGray.main",
                      }}
                    >
                      {record.refunded ? "Refunded" : "Purchased"}
                    </Typography>
                    <Typography
                      sx={{
                        textAlign: "center",
                        fontFamily: "ProximaNovaMedium",
                        fontSize: 14,
                        lineHeight: 1.25,
                        color: "gray.main",
                      }}
                    >
                      {new Date(
                        record.purchasedDate * 1000
                      ).toLocaleDateString()}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={1}
                  md={4}
                  sx={{
                    alignItems: "center",
                    justifyContent: "center",
                    display: "flex",
                  }}
                >
                  {record.isSuccessSession && (
                    <Typography
                      sx={{
                        display: { xs: "flex", sm: "flex", md: "none" },
                      }}
                    >
                      <Tooltip title="Payment Successful">
                        <CheckCircleOutlineIcon
                          sx={{ color: "success.main" }}
                        />
                      </Tooltip>
                    </Typography>
                  )}
                  {record.isSuccessSession && (
                    <Typography
                      sx={{
                        display: { xs: "none", sm: "none", md: "flex" },
                      }}
                    >
                      <CheckCircleOutlineIcon
                        sx={{ color: "success.main", mx: 1 }}
                      />
                      Payment Successful
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Paper>
          ))}

          {(!paymentrecords || paymentrecords.length < 1) && (
            <Typography
              sx={{
                fontSize: 18,
                paddingTop: 1,
                paddingBottom: 1,
                fontFamily: "ProximaNovaSoftMedium",
                color: "gray.main",
              }}
            >
              No Purchase History
            </Typography>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
